import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import AuthLayout from "./layouts/auth";
import AdminLayout from "./layouts/admin";
import SignIn from "./views/auth/signin";
import SignUp from "./views/auth/signup";
import ForgotPassword from "./views/auth/forgot-password";
import ResetPassword from "./views/auth/reset-password";
import VerifyEmail from "./views/auth/verify-email";
import TitleManager from "./components/TitleManager";
import { Box, Spinner } from "@chakra-ui/react";

// Restaurant slug redirect component
function RestaurantRedirect() {
  const { restaurantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableId = searchParams.get('tableId');

  useEffect(() => {
    // Eğer bu sayfa tarayıcıda açıldıysa, uygulama yüklü değil demektir
    // /download sayfasına yönlendir
    navigate(`/download/${restaurantSlug}${tableId ? `?tableId=${tableId}` : ''}`, { replace: true });
  }, [restaurantSlug, tableId, navigate]);

  return null;
}

// Code splitting - Landing sayfalarını lazy load et
// Not: Development modunda lazy loading daha yavaş olabilir, production build'de test edin
const MainLandingPage = lazy(() => import("./views/landing"));
const BusinessLandingPage = lazy(() => import("./views/business-landing"));
const DownloadPage = lazy(() => import("./views/download"));
const SupportPage = lazy(() => import("./views/support"));
const TermsOfService = lazy(() => import("./views/legal/TermsOfService"));
const KVKK = lazy(() => import("./views/legal/KVKK"));

// Loading component - minimal ve hızlı
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minH="100vh"
    bg="gray.50"
  >
    <Spinner size="xl" color="#E45858" thickness="4px" speed="0.8s" />
  </Box>
);

export default function App() {
  return (
    <>
      <TitleManager />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* Main Landing Page (Müşteriler için) */}
        <Route path="/" element={<MainLandingPage />} />
        
        {/* Business Landing Page (İşletmeler için) */}
        <Route path="/business" element={<BusinessLandingPage />} />

        {/* Download Page (Uygulama indirme) */}
        <Route path="/download/:restaurantSlug?" element={<DownloadPage />} />
        <Route path="/support" element={<SupportPage />} />

        {/* Legal Pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/kvkk" element={<KVKK />} />

      {/* Email Verification Route (Mobile App için) */}
      <Route path="/verify-email/:token" element={<VerifyEmail />} />

      {/* Auth Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="sign-in" element={<SignIn />} />
        <Route path="sign-up" element={<SignUp />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password/:token" element={<ResetPassword />} />
        <Route path="" element={<Navigate to="/auth/sign-in" replace />} />
      </Route>

      {/* Admin Routes with Business Name */}
      <Route path="/admin/:businessName/*" element={<AdminLayout />} />

      {/* Legacy Admin Route - Redirect to business name route if authenticated */}
      <Route path="/admin/*" element={<AdminLayout />} />

      {/* Restaurant slug route - eğer uygulama yüklü değilse /download'a yönlendir */}
      <Route path="/:restaurantSlug" element={<RestaurantRedirect />} />

      {/* Default Redirects */}
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Analytics />
      <SpeedInsights />
    </>
  );
}